import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styles from "./Chat.module.scss"
// import {HotelCard} from "./HotelCard";
import { HotelDetailsCard as HotelDetailCard } from "./HotelDetailsCard";
import {RoomCard} from "./RoomCard";
import {Slider} from "./Slider";
import {FlightContainer} from "./FlightContainer";
import {seedMessages} from "../seed";
import type { FilterPhase } from '../types/filterSidebar';
import {DestinationCardSmall} from "./DestinationCardSmall";
import seedDestination from "../seedDestination";
import {cancelExecution, executeFlightDetailsPlaybook, executeHotelCardsPlaybook, executeItineraryPlanner, loginToGateway, SESSION_TOKEN_KEY} from "../services/api";
import { DateRangePickerWidget } from './DateRangePickerWidget';
import { PlaceListWidget } from './PlaceListWidget';
import { PartyPickerWidget } from './PartyPickerWidget';
import { FlightContainerWidget } from './FlightContainerWidget';
import { FlightSearchForm } from './FlightSearchForm';
import type { FlightSearchCriteria } from './FlightSearchForm';
import { HOTEL_SEED } from '../data/hotelSeed';
import { FLIGHT_SEED } from '../data/flightSeed';
import { FlightDetailsStep } from './FlightDetailsStep';
import type { FlightDetailsResult } from './FlightDetailsStep';
import { FlightBookingWidget } from './FlightBookingWidget';
import type { FlightBookingResult } from './FlightBookingWidget';
import { Modal } from './Modal';
import type { FlightCardPayload } from './FlightCardWidget';
import type { ChatHistorySummary, SidebarView } from './FlightSearchSummary';
import {AlertTriangle, ArrowRight, Mountain, Palmtree, RefreshCw, Sparkles, UtensilsCrossed} from "lucide-react";
import { detectFarCityInMessage, getUserLocation, KNOWN_CITIES } from '../utilities/cityDetection';
import {HotelInfo} from "./HotelInfo";
import {HotelExpanded} from "../unused_components/HotelExpanded";
import {HotelCard} from "../unused_components/HotelCard";
import { useProfile } from '../context/ProfileContext';
import { bookingFromOrderPayload } from '../utilities/bookingFromOrder';

// Types for messages
export type ChatMessage =
    | { id?: string; view?: SidebarView; type: 'text'; text: string; fromUser: boolean }
    | { id?: string; view?: SidebarView; type: 'hotels'; hotels: any[] }
    | { id?: string; view?: SidebarView; type: 'rooms'; rooms: any[]; hotelId?: string }
    | { id?: string; view?: SidebarView; type: 'flight'; flights: any[] }
    | { id?: string; view?: SidebarView; executionId?: string; type: 'widget'; envelope: Record<string, unknown> }
    | { id?: string; view?: SidebarView; type: 'error'; text: string; detail?: string }
    | { id?: string; view?: SidebarView; type: 'other'; component: React.ReactNode };

type ChatProps = {
    activeView?: SidebarView;
    onSummaryChange?: (summary: ChatHistorySummary) => void;
    onSlotStateChange?: (slotState: Record<string, unknown>) => void;
    onContentTypeChange?: (phase: FilterPhase) => void;
    onStaticDatesChange?: (dates: { checkIn: string; checkOut: string }) => void;
    staticCheckIn?: string;
    staticCheckOut?: string;
    scrollToMessageId?: string;
    onScrollHandled?: () => void;
    initialMessages?: ChatMessage[];
    onMessagesChange?: (messages: ChatMessage[]) => void;
    /** A message handed over via router state (home page, a destination
     * card, the profile page). Sent once, then cleared by the parent. */
    pendingMessage?: string;
    onPendingMessageHandled?: () => void;
};

const nextId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const messageId = (msg: ChatMessage, index: number) => msg.id || `seed-${index}`;

/**
 * Turns a thrown error into something a traveller can act on. The raw text
 * is kept as the detail line — it is usually the gateway's own words, and
 * hiding it makes the failure impossible to report.
 */
const describeError = (raw: string): string => {
    const message = raw.toLowerCase();
    if (message.includes('log in') || message.includes('id token')) return 'You need to sign in first';
    if (message.includes('session')) return "Adiona couldn't start a session";
    if (message.includes('failed to fetch') || message.includes('networkerror')) return "Can't reach Adiona right now";
    if (message.includes('did not complete in time')) return 'That took too long to plan';
    return 'Something went wrong';
};

const HOTEL_CARD_REQUIRED_KEYS = [
    'id', 'name', 'stars', 'bedType', 'price', 'breakfastIncluded', 'image',
    '__renderDetailCard', 'location', 'rating', 'ratingLabel', 'pricePerNight',
    'totalPrice', 'nights', 'amenities', 'description', 'category', 'rooms',
];

const validateHotelCardsShape = (hotels: Record<string, unknown>[]) => {
    return hotels.map((hotel, index) => ({
        index,
        id: String(hotel.id || `hotel-${index + 1}`),
        missing: HOTEL_CARD_REQUIRED_KEYS.filter((key) => !(key in hotel)),
    }));
};


export const Chat=({
    activeView = 'searches',
    onSummaryChange,
    onSlotStateChange,
    onContentTypeChange,
    onStaticDatesChange,
    staticCheckIn,
    staticCheckOut,
    scrollToMessageId,
    onScrollHandled,
    initialMessages,
    onMessagesChange,
    pendingMessage,
    onPendingMessageHandled,
}: ChatProps) => {
    const { isAuthenticated, getIdTokenClaims } = useAuth0();
    const { addBooking } = useProfile();
    const [messages, setMessages] = useState<ChatMessage[]>(() => initialMessages ?? []);
    const [inputValue, setInputValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [bookingFlight, setBookingFlight] = useState<FlightCardPayload | null>(null);
    // Step 2 of the demo flight checkout (passenger + payment), shown in the
    // same modal right after FlightDetailsStep's fare/baggage step.
    const [flightBookingStep, setFlightBookingStep] = useState<FlightCardPayload | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const threadIdRef = useRef(`chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
    const activeRequestRef = useRef<AbortController | null>(null);
    const activeExecutionIdRef = useRef<string | undefined>(undefined);
    const messageRefs = useRef(new Map<string, HTMLDivElement>());
    const [highlightedId, setHighlightedId] = useState<string | undefined>(undefined);
    // Last message the user actually sent, so a failed turn's "Try again"
    // can resend it without round-tripping through the composer state.
    const lastUserMessageRef = useRef<string | null>(null);
    // Dedupes order_confirmation envelopes so a re-rendered or replayed
    // turn never banks the same booking into the profile twice.
    const recordedBookingsRef = useRef(new Set<string>());

    const visibleMessages = useMemo(
        () =>
            activeView === 'orders'
                ? messages.filter((message) => message.type === 'widget' && String(message.envelope?.widget_type || '') === 'order_confirmation')
                : messages,
        [activeView, messages]
    );

    const summary = useMemo<ChatHistorySummary>(() => {
        const searches: ChatHistorySummary['searches'] = [];
        const orders: ChatHistorySummary['orders'] = [];
        for (let i = 0; i < messages.length; i += 1) {
            const msg = messages[i];
            if (msg.type !== 'widget') continue;
            const widgetType = String(msg.envelope?.widget_type || '');
            const payload = (msg.envelope?.payload || {}) as Record<string, unknown>;
            const id = messageId(msg, i);
            if (!widgetType) continue;

            if (widgetType === 'order_confirmation') {
                const ref = String(payload.booking_reference || payload.order_id || 'order');
                const currency = String(payload.total_currency || '');
                const total = payload.total_amount === null || payload.total_amount === undefined ? '' : String(payload.total_amount);
                orders.push({ id, label: `Booking ${ref}`, subtitle: `${currency} ${total}`.trim(), widgetType });
            } else if (widgetType === 'flight_list' || widgetType === 'flight_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} options` : 'flight';
                searches.push({ id, label: 'Flights', subtitle: count, widgetType });
            } else if (widgetType === 'hotel_list' || widgetType === 'hotel_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} stays` : 'hotels';
                searches.push({ id, label: 'Hotels', subtitle: count, widgetType });
            } else if (widgetType === 'place_list' || widgetType === 'place_card') {
                const count = Array.isArray(payload.items) ? `${payload.items.length} places` : 'places';
                searches.push({ id, label: 'Places', subtitle: count, widgetType });
            } else if (widgetType === 'itinerary_summary') {
                searches.push({ id, label: 'Itinerary summary', subtitle: '', widgetType });
            } else if (widgetType === 'calendar_view') {
                searches.push({ id, label: 'Calendar', subtitle: '', widgetType });
            }
        }
        return { searches, orders, threadId: threadIdRef.current };
    }, [messages]);

    // Scroll to bottom on new message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    useEffect(() => {
        onSummaryChange?.(summary);
    }, [onSummaryChange, summary]);
    useEffect(() => {
        onMessagesChange?.(messages);
    }, [onMessagesChange, messages]);
    useEffect(() => {
        if (!scrollToMessageId) return;
        const node = messageRefs.current.get(scrollToMessageId);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedId(scrollToMessageId);
            const timer = window.setTimeout(() => setHighlightedId(undefined), 1500);
            onScrollHandled?.();
            return () => window.clearTimeout(timer);
        }
        onScrollHandled?.();
        return undefined;
    }, [scrollToMessageId, onScrollHandled]);

    // Sends a message handed over from elsewhere in the app exactly once.
    // The ref (not just the `pendingMessage` prop) guards against sending
    // twice: StrictMode double-invokes this effect on mount, and the
    // parent's prop doesn't clear until its own re-render lands.
    const handledPendingRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!pendingMessage) {
            handledPendingRef.current = undefined;
            return;
        }
        if (handledPendingRef.current === pendingMessage) return;
        handledPendingRef.current = pendingMessage;
        onPendingMessageHandled?.();
        void submitMessage(pendingMessage);
    }, [pendingMessage]);
    // Messages start empty; content is driven by user input only

    // Auto resize textarea
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';  // reset height
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';  // grow height
        }
    };

    // Handle Enter key to submit
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitMessage();
        }
    };

    // Submit message and clear input. `overrideText` lets a suggestion chip
    // send its own text directly instead of round-tripping through
    // `inputValue` state (which wouldn't be visible to this call yet).
    const submitMessage = async (overrideText?: string) => {
        const text = overrideText ?? inputValue;
        if (!text.trim()) return;

        const userRequest = text.trim();
        lastUserMessageRef.current = userRequest;
        setMessages(prev => [...prev, { id: nextId('user'), view: 'searches', type: 'text', text: userRequest, fromUser: true }]);
        setInputValue('');

        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        const lower = userRequest.toLowerCase();

        // Keyword: "hotel" → fetch hotel cards from gateway hotel-cards playbook
        if (lower.includes('hotel')) {
            onContentTypeChange?.('hotels');
            setSubmitting(true);
            try {
                const cityMatch = KNOWN_CITIES.find((city) => lower.includes(city.name.toLowerCase()));
                const userLocation = cityMatch ? null : await getUserLocation();
                const runHotelCards = async (token: string) =>
                    executeHotelCardsPlaybook(token, {
                        // An explicit city is authoritative. Otherwise use the
                        // browser location so the playbook searches nearby.
                        city: cityMatch?.name || (userLocation ? 'Current location' : undefined),
                        latitude: cityMatch?.lat ?? userLocation?.lat,
                        longitude: cityMatch?.lng ?? userLocation?.lng,
                        radius: 20,
                        check_in: staticCheckIn || undefined,
                        check_out: staticCheckOut || undefined,
                    });

                const hotelResult = await (async () => {
                    const initialToken = await getGatewaySessionToken();
                    try {
                        return await runHotelCards(initialToken);
                    } catch (firstError: any) {
                        const message = String(firstError?.message || '').toLowerCase();
                        if (!message.includes('session expired')) throw firstError;
                        localStorage.removeItem(SESSION_TOKEN_KEY);
                        const refreshedToken = await getGatewaySessionToken(true);
                        return runHotelCards(refreshedToken);
                    }
                })();

                const hotels = Array.isArray(hotelResult.hotels)
                    ? hotelResult.hotels.map((hotel) => ({ ...hotel, __renderDetailCard: true }))
                    : [];

                if (process.env.NODE_ENV !== 'production') {
                    console.log('[hotel-cards] raw-result', hotelResult);
                    console.log('[hotel-cards] mapped-hotels', hotels);
                    console.log('[hotel-cards] shape-check', validateHotelCardsShape(hotels as Record<string, unknown>[]));
                }

                if (hotelResult.check_in && hotelResult.check_out) {
                    onStaticDatesChange?.({
                        checkIn: hotelResult.check_in,
                        checkOut: hotelResult.check_out,
                    });
                }

                setMessages(prev => [
                    ...prev,
                    {
                        id: nextId('assistant'),
                        view: 'searches' as SidebarView,
                        type: 'hotels' as const,
                        hotels: hotels.length > 0
                            ? hotels
                            : HOTEL_SEED.map(h => ({ ...h, __renderDetailCard: true })),
                    },
                ]);

                if (!hotels.length) {
                    const providerReason = hotelResult.provider_error === null || hotelResult.provider_error === undefined
                        ? ''
                        : String(hotelResult.provider_error);
                    setMessages(prev => [
                        ...prev,
                        {
                            id: nextId('assistant'),
                            view: 'searches',
                            type: 'text',
                            text: providerReason
                                ? `No hotels were returned from live provider (${providerReason}) [execution ${hotelResult.executionId}${hotelResult.hotelbeds_env ? `, env ${hotelResult.hotelbeds_env}` : ''}], showing fallback results.`
                                : `No hotels were returned from live provider [execution ${hotelResult.executionId}${hotelResult.hotelbeds_env ? `, env ${hotelResult.hotelbeds_env}` : ''}${hotelResult.source ? `, source ${hotelResult.source}` : ''}], showing fallback results.`,
                            fromUser: false,
                        },
                    ]);
                }
            } catch (error: any) {
                const detail = String(error?.message || 'Failed to load hotels.');
                setMessages(prev => [
                    ...prev,
                    { id: nextId('assistant'), view: 'searches', type: 'error', text: describeError(detail), detail },
                ]);
            } finally {
                setSubmitting(false);
            }
            return;
        }

        // Keyword: "destination" → show destination cards from local seed data
        if (lower.includes('destination')) {
            onContentTypeChange?.('destinations');
            setMessages(prev => [
                ...prev,
                {
                    id: nextId('assistant'),
                    view: 'searches' as SidebarView,
                    type: 'other' as const,
                    component: (
                        <Slider slidesToShow={3}>
                            {seedDestination.map((dest, i) => (
                                <DestinationCardSmall destination={dest} key={i} />
                            ))}
                        </Slider>
                    ),
                },
            ]);
            return;
        }

        // Keyword: "flight" → show the flight search form so the traveller
        // picks trip type / airports / dates / travelers before we hand a
        // submitted_value turn to the gateway (same pattern DateRangePickerWidget
        // and PartyPickerWidget already use for their own submissions).
        if (lower.includes('flight')) {
            onContentTypeChange?.('flights');
            // A widget message rather than `other`: `other` carries a React
            // node, which can't be saved, so the form vanished whenever the
            // chat was reopened. This one round-trips through storage and
            // carries the submitted `criteria` so the selections come back.
            setMessages(prev => [
                ...prev,
                {
                    id: nextId('assistant'),
                    view: 'searches' as SidebarView,
                    type: 'widget' as const,
                    envelope: { widget_type: 'flight_search_form', payload: {} },
                },
            ]);
            return;
        }

        // Check if the message contains a known city >300 miles from the user's location
        // If found, the itinerary planner will handle flight suggestions
        const farCity = await detectFarCityInMessage(userRequest);
        if (farCity) {
            setMessages(prev => [
                ...prev,
                {
                    id: nextId('assistant'),
                    view: 'searches' as SidebarView,
                    type: 'text' as const,
                    text: `Searching for flights and trip options to ${farCity.name}…`,
                    fromUser: false,
                },
            ]);
        }

        // Call the gateway itinerary planner for all other messages
        // (and also for distant-city messages after showing the status hint)
        await handleAiMessage(userRequest);
    };
    const handleRoomsClick = (hotelId: string) => {
        console.log("inside function");
        // Find the hotel message in messages array
        const hotelMessage = messages.find(
            (msg): msg is { type: 'hotels'; hotels: any[] } =>
                msg.type === 'hotels' && msg.hotels.some(h => h.id === hotelId)
        );

        if (!hotelMessage) return;

        // Find the specific hotel and get its rooms
        const hotel = hotelMessage.hotels.find(h => h.id === hotelId) || HOTEL_SEED.find((h) => h.id === hotelId);
        if (!hotel || !hotel.rooms || hotel.rooms.length === 0) return;

        // Append a new message of type 'rooms' with these rooms
        console.log("end of function")
        onContentTypeChange?.('rooms');
        setMessages(prev => [...prev, { id: nextId('assistant'), view: 'searches', type: 'rooms', rooms: hotel.rooms, hotelId }]);
    };
    const getGatewaySessionToken = async (forceRefresh = false): Promise<string> => {
        const stored = (localStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
        if (stored && !forceRefresh) return stored;

        if (!isAuthenticated) {
            throw new Error('Please log in from the header and try again.');
        }

        const claims = await getIdTokenClaims();
        const idToken = claims?.__raw || '';
        if (!idToken) {
            throw new Error('Missing Auth0 ID token from current session.');
        }

        return loginToGateway(idToken, process.env.REACT_APP_AUTH0_DOMAIN);
    };

    //this code is to test GraphQL using open source country data

    // const handleSearch = async () => {
    //     const data = await client.request<{ country: Country }>(
    //         GET_COUNTRY,
    //         { code: "BR"}
    //     );
    //     setResult(data.country);
    // };

    // console.log(result);
    const handleAiMessage = async (userMessage?: string, turn?: { eventType?: 'user_message' | 'user_widget_submit' | 'user_widget_cta_click'; eventPayload?: Record<string, unknown> }) => {
        const controller = new AbortController();
        activeRequestRef.current?.abort();
        activeRequestRef.current = controller;
        activeExecutionIdRef.current = undefined;
        setSubmitting(true);
        try {
            const runPlanner = async (token: string) =>
                executeItineraryPlanner(token, {
                    threadId: threadIdRef.current,
                    userMessage: userMessage ?? '',
                    eventType: turn?.eventType || 'user_message',
                    eventPayload: turn?.eventPayload,
                }, {
                    signal: controller.signal,
                    onExecutionId: (id) => { activeExecutionIdRef.current = id; },
                });

            const result = await (async () => {
                const initialToken = await getGatewaySessionToken();
                try {
                    return await runPlanner(initialToken);
                } catch (firstError: any) {
                    const message = String(firstError?.message || '').toLowerCase();
                    if (!message.includes('session expired')) throw firstError;
                    localStorage.removeItem(SESSION_TOKEN_KEY);
                    const refreshedToken = await getGatewaySessionToken(true);
                    return runPlanner(refreshedToken);
                }
            })();

            console.log('EXECUTION_START_RAW', result.startResponse);
            console.log('EXECUTION_RESULT_RAW', result.executionResult);
            console.log('EXECUTION_RESULT_SNAPSHOT', result.snapshot);
            console.log('FINAL_TRAVEL_DATA', {
                executionId: result.executionId,
                status: result.status,
                source: result.extracted.source,
                envelope: result.extracted.envelope,
                slotState: result.extracted.slotState,
                botMessage: result.extracted.botMessage,
                result: (result.executionResult as any)?.result || null,
                data: (result.executionResult as any)?.data || null,
                events: Array.isArray((result.executionResult as any)?.events) ? (result.executionResult as any).events : [],
            });

            setMessages(prev => {
                const next = [...prev];
                const botText = (result.extracted.botMessage || '').trim();
                const envelopeWidgetType = String((result.extracted.envelope as Record<string, unknown> | null)?.widget_type || '');
                const messageView: SidebarView = envelopeWidgetType === 'order_confirmation' ? 'orders' : 'searches';

                if (botText) {
                    next.push({ id: nextId('assistant'), view: messageView, type: 'text', text: botText, fromUser: false } as ChatMessage);
                }
                if (result.extracted.envelope && typeof result.extracted.envelope === 'object') {
                    next.push({
                        id: nextId('assistant'),
                        view: messageView,
                        executionId: result.executionId,
                        type: 'widget',
                        envelope: result.extracted.envelope,
                    });
                    // Notify parent of the content phase based on widget type
                    if (envelopeWidgetType === 'flight_list' || envelopeWidgetType === 'flight_card') {
                        onContentTypeChange?.('flights');
                    } else if (envelopeWidgetType === 'hotel_list' || envelopeWidgetType === 'hotel_card') {
                        onContentTypeChange?.('hotels');
                    } else if (envelopeWidgetType === 'place_list' || envelopeWidgetType === 'place_card') {
                        onContentTypeChange?.('destinations');
                    }
                }
                if (result.extracted.slotState && typeof result.extracted.slotState === 'object') {
                    const nextSlotState = result.extracted.slotState as Record<string, unknown>;
                    onSlotStateChange?.(nextSlotState);

                    const checkIn = String(
                        nextSlotState.check_in_date
                        || nextSlotState.checkInDate
                        || nextSlotState.check_in
                        || ''
                    );
                    const checkOut = String(
                        nextSlotState.check_out_date
                        || nextSlotState.checkOutDate
                        || nextSlotState.check_out
                        || ''
                    );
                    if (checkIn || checkOut) {
                        onStaticDatesChange?.({
                            checkIn: checkIn || staticCheckIn || '',
                            checkOut: checkOut || staticCheckOut || '',
                        });
                    }
                }
                return next;
            });

            // Bank a confirmed booking into the profile so the trip shows up
            // under Profile → Trips. Done outside the state updater because
            // that callback must stay free of side effects.
            const envelope = result.extracted.envelope as Record<string, unknown> | null;
            if (envelope && String(envelope.widget_type || '') === 'order_confirmation') {
                const booking = bookingFromOrderPayload((envelope.payload || {}) as Record<string, unknown>);
                if (booking && booking.reference && !recordedBookingsRef.current.has(booking.reference)) {
                    recordedBookingsRef.current.add(booking.reference);
                    addBooking(booking);
                }
            }
        } catch (error: any) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                // Request was cancelled, do nothing
            } else {
                const detail = String(error?.message || 'Failed to run playbook.');
                setMessages(prev => [
                    ...prev,
                    { id: nextId('assistant'), view: 'searches', type: 'error', text: describeError(detail), detail },
                ]);
            }
        } finally {
            if (activeRequestRef.current === controller) {
                activeRequestRef.current = null;
                activeExecutionIdRef.current = undefined;
                setSubmitting(false);
            }
        }
    };

    const cancelRequest = () => {
        const executionId = activeExecutionIdRef.current;
        if (executionId) {
            void cancelExecution(executionId).catch(() => undefined);
        }
        activeRequestRef.current?.abort();
        activeRequestRef.current = null;
        activeExecutionIdRef.current = undefined;
        setSubmitting(false);
    };

    const handleDateRangeSubmit = async (value: { from: string; to: string; nights: number }) => {
        onStaticDatesChange?.({ checkIn: value.from, checkOut: value.to });
        await handleAiMessage(undefined, {
            eventType: 'user_widget_submit',
            eventPayload: {
                action_id: 'submit_dates',
                value,
                submitted_value: value,
            },
        });
    };

    const handlePlaceSelect = async (value: { label: string; id: string; kind: 'airport' | 'city' | 'landmark' }) => {
        await handleAiMessage(undefined, {
            eventType: 'user_widget_cta_click',
            eventPayload: {
                action_id: 'add_to_itinerary',
                place_id: value.id,
                label: value.label,
                kind: value.kind,
                value,
                submitted_value: value,
            },
        });
    };

    const handlePartySubmit = async (value: { rooms: number; adults: number; children: { age: number }[] }) => {
        await handleAiMessage(undefined, {
            eventType: 'user_widget_submit',
            eventPayload: {
                action_id: 'submit_party',
                value,
                submitted_value: value,
            },
        });
    };

    const handleFlightSearchSubmit = async (criteria: FlightSearchCriteria, formMessageId?: string) => {
        // Remember what was searched on the form's own message, so the
        // selections stay visible in the thread and survive a reload.
        if (formMessageId) {
            setMessages((prev) => prev.map((m) =>
                m.id === formMessageId && m.type === 'widget'
                    ? { ...m, envelope: { ...m.envelope, payload: { ...(m.envelope.payload as Record<string, unknown>), criteria } } }
                    : m
            ));
        }
        const controller = new AbortController();
        activeRequestRef.current?.abort();
        activeRequestRef.current = controller;
        activeExecutionIdRef.current = undefined;
        setSubmitting(true);
        try {
            const runFlightDetails = async (token: string) => executeFlightDetailsPlaybook(token, criteria, {
                signal: controller.signal,
                onExecutionId: (id) => { activeExecutionIdRef.current = id; },
            });
            const result = await (async () => {
                const initialToken = await getGatewaySessionToken();
                try {
                    return await runFlightDetails(initialToken);
                } catch (firstError: any) {
                    if (!String(firstError?.message || '').toLowerCase().includes('session expired')) throw firstError;
                    localStorage.removeItem(SESSION_TOKEN_KEY);
                    return runFlightDetails(await getGatewaySessionToken(true));
                }
            })();

            const offers = result.offers as FlightCardPayload[];
            const directions = offers.map((offer) => String((offer as any).direction || '').toLowerCase());
            const isRoundTrip = criteria.tripType === 'round_trip' || directions.some((direction) => ['outbound', 'inbound', 'roundtrip', 'round-trip'].includes(direction));
            const outbound = offers.filter((offer) => String((offer as any).direction || '').toLowerCase() === 'outbound');
            const inbound = offers.filter((offer) => String((offer as any).direction || '').toLowerCase() === 'inbound');
            const outboundItems = isRoundTrip && outbound.length ? outbound : offers;
            const hasReturnFlights = inbound.length > 0;
            const payload = {
                title: hasReturnFlights ? 'Outbound flights' : 'Flights',
                items: outboundItems,
                ...(inbound.length ? { return_items: inbound } : {}),
                ...(hasReturnFlights ? { direction: 'roundtrip' } : {}),
                total_count: outboundItems.length,
                currency: result.currency || String((outboundItems[0] as any)?.price?.currency || 'USD'),
            };

            setMessages((prev) => [...prev, {
                id: nextId('assistant'),
                view: 'searches',
                executionId: result.executionId,
                type: 'widget',
                envelope: { widget_type: 'flight_list', payload },
            }]);
            onContentTypeChange?.('flights');
        } catch (error: any) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                const detail = String(error?.message || 'Failed to load flights.');
                setMessages((prev) => [...prev, {
                    id: nextId('assistant'), view: 'searches', type: 'error', text: describeError(detail), detail,
                }]);
            }
        } finally {
            if (activeRequestRef.current === controller) {
                activeRequestRef.current = null;
                activeExecutionIdRef.current = undefined;
                setSubmitting(false);
            }
        }
    };

    const handleFlightAction = async (actionId: string, value?: Record<string, unknown>) => {
        // Intercept view_offer to show Flight Details step first
        if (actionId.startsWith('view_offer:')) {
            const offerId = String(value?.offer_id || actionId.slice('view_offer:'.length));
            // Find the flight payload from messages
            const flightPayload = findFlightPayload(offerId);
            if (flightPayload) {
                const outbound = value?.outbound_offer as FlightCardPayload | undefined;
                // price/baggage_options/conditions_options/fare_brand are all
                // offer-level in the API (one set per direction), not
                // itinerary-level, so they have to be copied onto each leg's
                // own itinerary here -- once the two offers are merged into
                // one booking object below, `booking.price` becomes their
                // COMBINED total and only one offer's other top-level fields
                // would otherwise survive at all. FlightDetailsStep reads
                // each leg's own price/baggage/conditions off its itinerary
                // so the outbound and return fare rows price independently.
                const stampLeg = (offer: FlightCardPayload) =>
                    (offer.itineraries || []).map((it) => ({
                        ...it,
                        price: it.price ?? offer.price,
                        baggage_options: it.baggage_options ?? offer.baggage_options,
                        baggage_known: it.baggage_known ?? offer.baggage_known,
                        conditions_options: it.conditions_options ?? offer.conditions_options,
                        conditions_known: it.conditions_known ?? offer.conditions_known,
                        fare_brand: it.fare_brand ?? offer.fare_brand,
                    }));
                if (outbound && outbound.itineraries?.length && flightPayload.itineraries?.length) {
                    const outboundTotal = Number(outbound.price?.total || 0);
                    const returnTotal = Number(flightPayload.price?.total || 0);
                    setBookingFlight({
                        ...flightPayload,
                        offer_id: `${outbound.offer_id}__${flightPayload.offer_id}`,
                        direction: 'round-trip',
                        price: {
                            total: String(outboundTotal + returnTotal),
                            currency: outbound.price?.currency || flightPayload.price?.currency || 'USD',
                        },
                        itineraries: [...stampLeg(outbound), ...stampLeg(flightPayload)],
                        carriers: Array.from(new Set([...(outbound.carriers || []), ...(flightPayload.carriers || [])])),
                        stops: (outbound.stops || 0) + (flightPayload.stops || 0),
                    });
                } else {
                    setBookingFlight({ ...flightPayload, itineraries: stampLeg(flightPayload) });
                }
                return;
            }
        }
        await handleAiMessage(undefined, {
            eventType: 'user_widget_cta_click',
            eventPayload: {
                action_id: actionId,
                value,
                submitted_value: value,
            },
        });
    };

    const findFlightPayload = (offerId: string): FlightCardPayload | null => {
        for (const msg of messages) {
            if (msg.type !== 'widget') continue;
            const wt = String(msg.envelope?.widget_type || '');
            const payload = msg.envelope?.payload as any;
            if (wt === 'flight_list' && Array.isArray(payload?.items)) {
                const allItems = [...payload.items, ...(Array.isArray(payload.return_items) ? payload.return_items : [])];
                const found = allItems.find((item: any) => item.offer_id === offerId);
                if (found) return found as FlightCardPayload;
            }
            if (wt === 'flight_card' && payload?.offer_id === offerId) {
                return payload as FlightCardPayload;
            }
        }
        return null;
    };

    const handleFlightDetailsContinue = (_result: FlightDetailsResult) => {
        const flight = bookingFlight;
        setBookingFlight(null);
        if (!flight) return;
        // Hand off to the passenger/payment step (Step 2 of 5) in the same
        // modal, matching HotelBookingWidget's Reserve → Pay demo checkout —
        // no gateway call happens here.
        setFlightBookingStep(flight);
    };

    const handleFlightBookingContinue = (_result: FlightBookingResult) => {
        // The booking is already banked into the profile inside
        // FlightBookingWidget itself; this just closes the modal.
        setFlightBookingStep(null);
    };

    const retryLast = () => {
        const last = lastUserMessageRef.current;
        if (!last || submitting) return;
        void handleAiMessage(last);
    };

    const isEmptyThread = visibleMessages.length === 0 && !submitting;

    return (
        <div className={`${styles.chatContainer}${isEmptyThread ? ` ${styles.chatContainerEmpty}` : ''}`}>
            {/* The thread is the only thing that scrolls — the sidebars and
                the composer stay put while it moves. */}
            <div className={styles.chatScroll}>
            {isEmptyThread && (
                <div className={styles.emptyState}>
                    <h2 className={styles.emptyHeading}>Where are you headed?</h2>
                    <p className={styles.emptySubtext}>
                        Describe the trip in a sentence. Adiona handles the rest.
                    </p>

                    <div className={styles.categoryGrid}>
                        {[
                            { icon: <UtensilsCrossed size={17} />, label: 'Food & culinary trips', prompt: 'Best food and culinary destinations' },
                            { icon: <Palmtree size={17} />, label: 'Beach escape', prompt: 'Tropical beach escape for a week' },
                            { icon: <Mountain size={17} />, label: 'Adventure & nature', prompt: 'Adventure destination with hiking and nature' },
                            { icon: <Sparkles size={17} />, label: 'Romantic getaway', prompt: 'Romantic getaway for two' },
                        ].map(c => (
                            <button
                                key={c.label}
                                className={styles.categoryCard}
                                type="button"
                                onClick={() => setInputValue(c.prompt)}
                            >
                                <span className={styles.categoryIcon}>{c.icon}</span>
                                <span className={styles.categoryLabel}>{c.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className={styles.suggestedSection}>
                        <span className={styles.suggestedLabel}>Try asking</span>
                        <div className={styles.suggestedChips}>
                            {[
                                'Best destinations for solo travel in Asia',
                                'Flights to London in July, 2 people',
                                'Romantic beach honeymoon under $3,000',
                            ].map(q => (
                                <button
                                    key={q}
                                    className={styles.suggestedChip}
                                    type="button"
                                    onClick={() => void submitMessage(q)}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {visibleMessages.map((msg, i) => {
                const id = messageId(msg, i);
                const highlighted = highlightedId === id;
                return (
                    <div
                        key={id}
                        ref={(node) => {
                            if (node) messageRefs.current.set(id, node);
                            else messageRefs.current.delete(id);
                        }}
                        className={`${styles.chatMessage} ${highlighted ? styles.chatMessageHighlighted : ''}`}
                    >
                        {msg.type === 'text' && (
                            <div className={msg.fromUser ? styles.messageBubbleUser : styles.messageBubble}>
                                {msg.text}
                            </div>
                        )}

                    {msg.type === 'error' && (
                        <div className={styles.errorNotice} role="alert">
                            <span className={styles.errorIcon}><AlertTriangle size={16} aria-hidden="true" /></span>
                            <div className={styles.errorBody}>
                                <span className={styles.errorTitle}>{msg.text}</span>
                                {msg.detail && <span className={styles.errorDetail}>{msg.detail}</span>}
                            </div>
                            {lastUserMessageRef.current && (
                                <button
                                    type="button"
                                    className={styles.errorRetry}
                                    onClick={retryLast}
                                    disabled={submitting}
                                >
                                    <RefreshCw size={13} aria-hidden="true" /> Try again
                                </button>
                            )}
                        </div>
                    )}

                    {msg.type === 'hotels' && (
                        <Slider slidesToShow={3}>
                            {/*{msg.hotels.map(hotel => (*/}
                            {/*    <HotelCard key={hotel.id} hotel={hotel} onRoomsClick={handleRoomsClick} />*/}
                            {/*))}*/}
                            {msg.hotels.map((hotel) => (
                                hotel.__renderDetailCard === true
                                    ? <HotelDetailCard key={hotel.id} hotel={hotel} onViewRooms={(h) => handleRoomsClick(String(h?.id || ''))} />
                                    : null
                            ))}
                        </Slider>
                    )}

                    {msg.type === 'rooms' && msg.hotelId && msg.rooms && (
                        <Slider slidesToShow={3}>
                            {msg.rooms.map(room => (
                                <RoomCard
                                    key={room.id}
                                    room={room}
                                    staticCheckIn={staticCheckIn}
                                    staticCheckOut={staticCheckOut}
                                />
                            ))}
                        </Slider>
                    )}

                    {msg.type === 'flight' && (
                        <div><FlightContainer flights={msg.flights} /></div>
                    )}

                    {msg.type === 'other' && (
                        <>{msg.component}</>
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_search_form' && (
                        <FlightSearchForm
                            initialCriteria={(msg.envelope?.payload as { criteria?: FlightSearchCriteria } | undefined)?.criteria}
                            onSearch={(criteria) => handleFlightSearchSubmit(criteria, msg.id)}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'date_range_picker' && (
                        <DateRangePickerWidget
                            payload={(msg.envelope?.payload || {}) as any}
                            onSubmit={handleDateRangeSubmit}
                        />
                    )}

                    {msg.type === 'widget' && ['place_list', 'place_card'].includes(String(msg.envelope?.widget_type || '')) && (
                        <PlaceListWidget
                            payload={(String(msg.envelope?.widget_type || '') === 'place_card'
                            ? {
                                title: 'Place',
                                items: [((msg.envelope?.payload || {}) as Record<string, unknown>)],
                            }
                            : (msg.envelope?.payload || { title: 'Places', items: [] })) as any}
                            onAddPlace={handlePlaceSelect}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'party_picker' && (
                        <PartyPickerWidget
                            payload={(msg.envelope?.payload || { rooms_max: 9, adults_max: 9, children_max: 6, allow_child_ages: false }) as any}
                            onSubmit={handlePartySubmit}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_list' && (
                        <FlightContainerWidget
                            payload={(msg.envelope?.payload || { title: 'Flights', items: [], total_count: 0, currency: 'USD' }) as any}
                            onAction={handleFlightAction}
                        />
                    )}

                    {msg.type === 'widget' && String(msg.envelope?.widget_type || '') === 'flight_card' && (
                        <FlightContainerWidget
                            payload={{
                                title: 'Flight offer',
                                items: [(msg.envelope?.payload || {}) as any],
                                total_count: 1,
                                currency: String((msg.envelope?.payload as any)?.price?.currency || 'USD'),
                            }}
                            onAction={handleFlightAction}
                        />
                    )}
                    {msg.type === 'widget' && msg.executionId ? (
                        <div className={styles.executionId}>Execution {msg.executionId}</div>
                    ) : null}
                </div>
            )})}
            {/* Was inline as the next "message" in the thread — now a modal,
                matching how HotelDetailsCard and DestinationExpanded open
                their own detail views. Step 1 (fare/baggage) hands off to
                step 2 (passenger/payment) in the same modal. */}
            <Modal
                isOpen={Boolean(bookingFlight) || Boolean(flightBookingStep)}
                onClose={() => { setBookingFlight(null); setFlightBookingStep(null); }}
                size="pageNarrow"
            >
                {bookingFlight && (
                    <FlightDetailsStep
                        booking={bookingFlight}
                        onContinue={handleFlightDetailsContinue}
                    />
                )}
                {flightBookingStep && (
                    <FlightBookingWidget
                        booking={flightBookingStep}
                        onContinue={handleFlightBookingContinue}
                    />
                )}
            </Modal>
            <div ref={messagesEndRef} />

            {submitting && (
                <div className={styles.planningIndicator}>
                    <span className={styles.spinner} />
                    <span className={styles.planningText}>Adiona is planning</span>
                    <button className={styles.cancelButton} onClick={cancelRequest}>
                        Cancel
                    </button>
                </div>
            )}

            {/*this button triggers function and displays results of testing GraphQL using opensource country data.*/}

            {/*<button*/}
            {/*    onClick={handleSearch}*/}
            {/*>GrapgQL</button>*/}
            {/*/!* {sseClientId && (*/}
            {/*    <div className={styles.messageBubble}>SSE Client ID: {sseClientId}</div>*/}
            {/*)} *!/*/}
            {/*<Slider slidesToShow={3}>*/}
            {/*    {result && (*/}
            {/*        <DestinationCard destination={result} />*/}
            {/*    )}*/}
            {/*    {result && (*/}
            {/*        <DestinationCard destination={result} />*/}
            {/*    )}*/}
            {/*</Slider>*/}

            </div>
            {/* AI responses are appended in the main chat message list above */}
            {/* Search bar */}
            <div className={styles.chatInputWrapper}>
                <form
                    className={styles.inputAIForm}
                    onSubmit={(e) => {
                        e.preventDefault();
                        void submitMessage();
                    }}
                >
                    <textarea id="aiInput"
                              ref={textareaRef}
                              placeholder="Describe your travel plans..."
                              className={styles.textareaInputAI}
                              value={inputValue}
                              disabled={submitting}
                              onChange={handleInputChange}
                              onKeyDown={handleKeyDown}
                              rows={1}
                              style={{ overflow: 'hidden' }}
                    />
                    <button id="submitBtn"
                            type="submit"
                            className={styles.sendBtn}
                            disabled={submitting || !inputValue.trim()}
                            title="Submit Button">
                        <ArrowRight size={18} strokeWidth={2.5} />
                    </button>
                </form>
                <div className={styles.chatInputHint}>
                    <span>Press `Enter` to send</span>
                    <span>Press `Shift + Enter` for a new line</span>
                </div>
            </div>
        </div>
    );
}
